import React, { useEffect, useState, useRef } from "react";
import { transactionsService, metersService, importExportService } from "../services/api";
import { format } from "date-fns";
import { Plus, Loader, Download, Eye, Search as IconSearch, Copy, Printer, X, AlertCircle, ExternalLink, Zap } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { monthlyFeesService } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { PermissionGuard } from '../components/PermissionGuard';
import { PERMISSIONS } from '../utils/permissions';
import Pagination from '../components/Pagination';

const SENEGAL_TARIF_KWH = 120.0;
const PAGE_SIZE = 50;
const MINIMUM_PURCHASE_AMOUNT = 500;

// ✅ CORRIGÉ : remplace VICTRON_FORFAITS_UI (200F/1j, 1300F/7j, 5000F/30j, 14000F/90j)
// qui ne correspondait à RIEN côté backend. Le backend (victron_payg_service.py)
// vend par ABONNEMENT MENSUEL selon la souscription réellement configurée sur
// le compteur (S1/S2/S3), pas par forfait libre. Ces valeurs doivent rester
// identiques à VICTRON_SUBSCRIPTIONS dans services/victron_payg_service.py.
const VICTRON_SUBSCRIPTIONS = {
  S1: { label: "S1 · 75W", price: 4000 },
  S2: { label: "S2 · 150W", price: 6000 },
  S3: { label: "S3 · 200W", price: 9000 },
};

const Badge = ({ text, tone = "slate" }) => {
  const toneMap = {
    slate: "bg-slate-100 text-slate-800",
    green: "bg-green-100 text-green-700",
    yellow: "bg-yellow-100 text-yellow-700",
    red: "bg-red-100 text-red-700",
    blue: "bg-blue-100 text-blue-700"
  };
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${toneMap[tone] || toneMap.slate}`}>{text}</span>;
};

const isVictronMeter = (meter) => meter?.meter_brand?.toUpperCase() === "VICTRON";
// ✅ Compteur à eau GOMELONG : vente en m³, pas de redevance
const isWaterMeter = (meter) => (meter?.meter_type || "").toLowerCase() === "water";
const unitOf = (meter) => (isWaterMeter(meter) ? "m³" : "kWh");

/**
 * ✅ Calcule le montant redevance et le montant courant d'une transaction
 * Logique : montant_courant = kwh_amount * tariff
 *           montant_redevance = amount_total - montant_courant
 * (inchangé — cette logique ne s'applique pas aux transactions Victron,
 * qui n'ont pas de kwh_amount et sont gérées séparément à l'affichage)
 */
// const computeFeeBreakdown = (transaction) => {
//   const totalAmount = parseFloat(transaction.amount) || 0;
//   const kwhAmount   = parseFloat(transaction.kwh_amount) || 0;
//   const tariff      = parseFloat(transaction.meter?.tariff) || SENEGAL_TARIF_KWH;
//   const electricityRaw = kwhAmount * tariff;
//   const feeRaw = totalAmount - electricityRaw;
//   const feeAmount = feeRaw <= 1 ? 0 : Math.round(feeRaw);
//   const electricityAmount = totalAmount - feeAmount;
//   return { feeAmount, electricityAmount };
// };
const computeFeeBreakdown = (transaction) => {
  // ✅ Eau : pas de redevance, tout le montant = consommation
  if (isWaterMeter(transaction.meter)) {
    return transaction.status === "completed"
      ? { feeAmount: 0, electricityAmount: parseFloat(transaction.amount) || 0 }
      : { feeAmount: 0, electricityAmount: 0 };
  }
  // ✅ La répartition redevance/courant n'a de sens QUE pour une transaction
  // complétée. Pour pending/cancelled/failed, kwh_amount est 0/null et le
  // calcul retombait à tort sur "tout le montant = redevance".
  if (transaction.status !== "completed") {
    return { feeAmount: 0, electricityAmount: 0 };
  }
  const totalAmount = parseFloat(transaction.amount) || 0;
  const kwhAmount   = parseFloat(transaction.kwh_amount) || 0;
  const tariff      = parseFloat(transaction.meter?.tariff) || SENEGAL_TARIF_KWH;
  const electricityRaw = kwhAmount * tariff;
  const feeRaw = totalAmount - electricityRaw;
  const feeAmount = feeRaw <= 1 ? 0 : Math.round(feeRaw);
  const electricityAmount = totalAmount - feeAmount;
  return { feeAmount, electricityAmount };
};

const Transactions = () => {
  const { hasPermission } = usePermissions();
  const [transactions, setTransactions] = useState([]);
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [form, setForm] = useState({ meter_id: "", amount: "", payment_method: "cash" });
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [historyForMeter, setHistoryForMeter] = useState([]);
  const [estimatedKwh, setEstimatedKwh] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [meterBlocked, setMeterBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [debtInfo, setDebtInfo] = useState(null);
  const [loadingDebt, setLoadingDebt] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [meterBrand, setMeterBrand] = useState("all");
  const tableRef = useRef(null);
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [meterSearch, setMeterSearch] = useState("");
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [amountError, setAmountError] = useState("");
  // ✅ NOUVEAU : nombre de mois choisi pour un abonnement Victron
  const [victronMonths, setVictronMonths] = useState(1);

  useEffect(() => {
    fetchMeters();
    setPage(1);
  }, [query, statusFilter, methodFilter, zoneFilter, startDate, endDate]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, paymentMethod, startDate, endDate]);

  const checkMeterDebt = async (meterId, forceRefresh = false) => {
    if (!meterId) {
      setDebtInfo(null);
      setMeterBlocked(false);
      setBlockReason("");
      return;
    }
    try {
      setLoadingDebt(true);
      const response = await monthlyFeesService.checkDebt(meterId, forceRefresh);
      const data = response.data;
      setDebtInfo(data);
      if (data.meter_blocked) {
        setMeterBlocked(true);
        setBlockReason("Ce compteur est bloqué pour non-paiement de redevances.");
      } else {
        setMeterBlocked(false);
        setBlockReason("");
      }
      if (data.total_debt > 0) {
        toast.warning(
          `⚠️ Dette en cours : ${new Intl.NumberFormat('fr-FR').format(data.total_debt)} FCFA`,
          { autoClose: 5000 }
        );
      } else {
        toast.success(`✅ Client à jour - Aucune redevance impayée`, { autoClose: 3000 });
      }
      return data;
    } catch (error) {
      console.error('Erreur vérification dette:', error);
      toast.error('Impossible de vérifier la dette');
      setDebtInfo(null);
      setMeterBlocked(false);
      setBlockReason("");
    } finally {
      setLoadingDebt(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransactions();
    }, 300);
    return () => clearTimeout(timer);
  }, [page, query, statusFilter, paymentMethod, startDate, endDate, refreshFlag]);

  // ✅ CORRIGÉ : pour Victron, le montant se déduit de victronMonths x prix
  // de la souscription du compteur, pas d'une saisie libre.
  useEffect(() => {
    if (isVictronMeter(selectedMeter)) {
      const sub = VICTRON_SUBSCRIPTIONS[selectedMeter.victron_subscription];
      if (sub) {
        const amount = sub.price * victronMonths;
        setForm((f) => ({ ...f, amount: String(amount) }));
      }
      return;
    }
    if (form.amount) {
      validateAmount(form.amount);
    }
    computeKwh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount, selectedMeter, form.meter_id, meters, debtInfo, victronMonths]);

  const submitTransaction = async () => {
    if (!hasPermission(PERMISSIONS.CREATE_SALE)) {
      toast.error('Permission refusée');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        meter_id: form.meter_id,
        amount: parseFloat(form.amount),
        payment_method: form.payment_method,
      };
      // ✅ Réutilise l'endpoint générique POST /transactions : sa branche
      // Victron (routers/transactions.py) valide déjà que le montant est
      // un multiple du prix mensuel de la souscription du compteur, génère
      // le token OpenPAYGO et étend victron_expiry_date. Pas besoin d'appeler
      // un endpoint séparé /victron-payg/pay-subscription depuis le frontend.
      const res = await transactionsService.create(payload);
      const txn = res.data;
      if (txn.token) {
        toast.success(`✅ Token: ${txn.token}`, { autoClose: 8000 });
      }
      if (txn.victron_expiry_date) {
        toast.info(
          `📅 Abonnement Victron actif jusqu'au ${new Date(txn.victron_expiry_date).toLocaleDateString('fr-FR')}`,
          { autoClose: 8000 }
        );
      }
      setConfirmOpen(false);
      setModalOpen(false);
      setDebtInfo(null);
      setMeterBlocked(false);
      setBlockReason("");
      setSelectedMeter(null);
      setVictronMonths(1);
      setForm({ meter_id: "", amount: "", payment_method: "cash" });
      setRefreshFlag(f => f + 1);
    } catch (e) {
      console.error(e);
      const errorMsg = e.response?.data?.detail?.message ||
        e.response?.data?.error ||
        'Erreur transaction';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (!amount || amount === "") { setAmountError(""); return false; }
    if (isNaN(numAmount)) { setAmountError("⚠️ Montant invalide"); return false; }

    // ✅ CORRIGÉ : Victron — le montant doit être un multiple du prix mensuel
    // de la souscription CONFIGURÉE sur le compteur (plus de forfaits libres).
    if (isVictronMeter(selectedMeter)) {
      const sub = VICTRON_SUBSCRIPTIONS[selectedMeter?.victron_subscription];
      if (!sub) {
        setAmountError("❌ Ce compteur Victron n'a pas de souscription configurée. Configurez-la dans Compteurs.");
        return false;
      }
      if (numAmount < sub.price || numAmount % sub.price !== 0) {
        setAmountError(`❌ Le montant doit être un multiple de ${sub.price.toLocaleString()} FCFA (${sub.label})`);
        return false;
      }
      setAmountError("");
      return true;
    }

    if (numAmount < MINIMUM_PURCHASE_AMOUNT) {
      setAmountError(`❌ Montant minimum: ${MINIMUM_PURCHASE_AMOUNT} FCFA`);
      return false;
    }
    if (debtInfo && debtInfo.total_debt > 0) {
      const amountAfterDebt = numAmount - debtInfo.total_debt;
      if (amountAfterDebt < 0) {
        setAmountError(`❌ Montant insuffisant pour dette: ${debtInfo.total_debt.toFixed(0)} FCFA`);
        return false;
      }
      if (amountAfterDebt > 0 && amountAfterDebt < MINIMUM_PURCHASE_AMOUNT) {
        const minimumTotal = debtInfo.total_debt + MINIMUM_PURCHASE_AMOUNT;
        setAmountError(
          `⚠️ Après paiement dette (${debtInfo.total_debt.toFixed(0)} FCFA), ` +
          `il doit rester au moins ${MINIMUM_PURCHASE_AMOUNT} FCFA. ` +
          `Total minimum: ${minimumTotal.toFixed(0)} FCFA`
        );
        return false;
      }
    }
    setAmountError("");
    return true;
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        status_filter: statusFilter,
        payment_method: paymentMethod,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: query.length >= 3 ? query : undefined
      };
      const res = await transactionsService.getAll(params);
      const data = res.data;
      setTransactions(data.items);
      setTotalItems(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      toast.error("Erreur chargement transactions");
      setTransactions([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeters = async (searchTerm = "") => {
    if (loadingMeters) return;
    try {
      setLoadingMeters(true);
      if (!searchTerm || searchTerm.trim().length < 2) {
        setLoadingMeters(false);
        return;
      }
      const params = { limit: 50, skip: 0, search: searchTerm.trim() };
      const res = await metersService.getAll(params);
      const data = res.data || res;
      if (Array.isArray(data.items)) setMeters(data.items);
      else if (Array.isArray(data)) setMeters(data);
      else setMeters([]);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les compteurs");
      setMeters([]);
    } finally {
      setLoadingMeters(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (modalOpen && meterSearch.length >= 2) fetchMeters(meterSearch);
      else if (modalOpen && meterSearch.length === 0) setMeters([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [meterSearch, modalOpen]);

  const fetchMeterDetail = async (meterId) => {
    if (!meterId) { setSelectedMeter(null); setHistoryForMeter([]); return; }
    try {
      const res = await metersService.getById(meterId);
      const meter = res.data || res;
      setSelectedMeter(meter);
      const txRes = await transactionsService.getAll({ meter_id: meterId, limit: 10 });
      const recent = txRes.data?.items ?? txRes.data ?? txRes;
      setHistoryForMeter(Array.isArray(recent) ? recent : []);
      if (meter.status === "suspended") {
        setMeterBlocked(true);
        setBlockReason("Ce compteur est bloqué pour non-paiement.");
      } else {
        setMeterBlocked(false);
        setBlockReason("");
      }
    } catch (e) {
      toast.error("Erreur chargement compteur");
    }
  };

  const computeKwh = () => {
    if (isVictronMeter(selectedMeter)) { setEstimatedKwh(0); return; }
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { setEstimatedKwh(0); return; }
    let tarif = SENEGAL_TARIF_KWH;
    if (selectedMeter?.tariff > 0) tarif = selectedMeter.tariff;
    let amountForElectricity = amount;
    if (debtInfo && debtInfo.total_debt > 0) {
      amountForElectricity = Math.max(0, amount - debtInfo.total_debt);
    }
    setEstimatedKwh(amountForElectricity / tarif);
  };

  const openCreateModal = () => {
    if (!hasPermission(PERMISSIONS.CREATE_SALE)) {
      toast.error('Vous n\'avez pas la permission de créer des ventes');
      return;
    }
    setForm({ meter_id: "", amount: "", payment_method: "cash" });
    setSelectedMeter(null);
    setHistoryForMeter([]);
    setEstimatedKwh(0);
    setMeterSearch("");
    setMeters([]);
    setDebtInfo(null);
    setMeterBlocked(false);
    setBlockReason("");
    setLoadingDebt(false);
    setVictronMonths(1);
    setModalOpen(true);
  };

  const onChangeMeter = async (meterId) => {
    setForm((prev) => ({ ...prev, meter_id: meterId, amount: "" }));
    setVictronMonths(1);
    await fetchMeterDetail(meterId);
    const meter = meters.find(m => m.meter_id === meterId);
    // ✅ Ne pas vérifier la dette pour Victron (abonnement) ni pour l'eau (pas de redevance)
    if (!isVictronMeter(meter) && !isWaterMeter(meter)) {
      await checkMeterDebt(meterId, true);
    } else {
      setDebtInfo(null);
      setMeterBlocked(false);
      setBlockReason("");
    }
  };

  const onConfirm = () => {
    if (!validateAmount(form.amount)) {
      toast.error(amountError || "Montant invalide");
      return;
    }
    setConfirmOpen(true);
  };

  const MeterStatusBadge = ({ status }) => {
    if (status === "suspended") {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-semibold">🚫 BLOQUÉ</span>;
    }
    if (status === "active") {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">✅ ACTIF</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">INACTIF</span>;
  };

  const DebtAlert = ({ debtInfo }) => {
    if (!debtInfo || debtInfo.total_debt === 0) return null;
    const canProceed = parseFloat(form.amount) >= debtInfo.total_debt;
    return (
      <div className={`border-2 rounded-lg p-4 mb-4 ${canProceed ? 'bg-blue-50 border-blue-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className={`w-6 h-6 flex-shrink-0 ${canProceed ? 'text-blue-600' : 'text-red-600'}`} />
          <div className="flex-1">
            <h4 className={`font-bold mb-2 ${canProceed ? 'text-blue-900' : 'text-red-900'}`}>
              {canProceed ? '✅ Dette couverte' : '⚠️ Redevances impayées'}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">Dette totale:</span>
                <span className={`font-bold ${canProceed ? 'text-blue-900' : 'text-red-900'}`}>
                  {new Intl.NumberFormat('fr-FR').format(debtInfo.total_debt)} FCFA
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Nombre de mois:</span>
                <span className="font-bold">{debtInfo.unpaid_fees_count} mois</span>
              </div>
              {parseFloat(form.amount) > 0 && (
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span>Montant fourni:</span>
                    <span className="font-bold">{form.amount} FCFA</span>
                  </div>
                  <div className="flex justify-between text-sm text-red-700">
                    <span>- Redevances:</span>
                    <span className="font-bold">{Math.min(parseFloat(form.amount), debtInfo.total_debt).toFixed(0)} FCFA</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-700 border-t pt-1">
                    <span>= Électricité:</span>
                    <span className="font-bold">{Math.max(0, parseFloat(form.amount) - debtInfo.total_debt).toFixed(0)} FCFA</span>
                  </div>
                </div>
              )}
            </div>
            {canProceed ? (
              <div className="mt-3 p-2 bg-blue-100 rounded border border-blue-200">
                <p className="text-xs font-bold text-blue-900">✅ Montant suffisant. Transaction autorisée.{debtInfo.meter_blocked && ' Compteur sera débloqué.'}</p>
              </div>
            ) : (
              <div className="mt-3 p-2 bg-red-100 rounded border border-red-200">
                <p className="text-xs font-bold text-red-900">❌ Montant insuffisant. Minimum: {debtInfo.total_debt.toFixed(0)} FCFA</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleExportCSV = async () => {
    try {
      if (totalItems > 5000) {
        const userConfirmed = window.confirm(
          `⚠️ EXPORT VOLUMINEUX\n\nVous allez exporter ${totalItems.toLocaleString()} transactions en CSV.\n\nVoulez-vous continuer ?`
        );
        if (!userConfirmed) { toast.info("Export annulé"); return; }
      }
      const loadingToast = toast.info(
        totalItems > 1000 ? '📥 Export en cours... Cela peut prendre quelques secondes' : '📥 Export en cours...',
        { autoClose: false, toastId: 'csv-export' }
      );
      const res = await transactionsService.getAll({
        skip: 0,
        limit: Math.min(totalItems, 10000),
        status_filter: statusFilter,
        payment_method: paymentMethod,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search: query.length >= 3 ? query : undefined
      });
      const allTransactions = res.data?.items || transactions;
      const headers = [
        "Date", "ID Transaction", "Client", "Téléphone", "Compteur", "Marque",
        "Montant Total (FCFA)", "Redevance (FCFA)", "Montant Courant (FCFA)",
        "Volume", "Unité", "Méthode", "Token", "Statut", "État Compteur"
      ];
      const rows = allTransactions.map((t) => {
        const { feeAmount, electricityAmount } = computeFeeBreakdown(t);
        return [
          format(new Date(t.created_at), "dd/MM/yyyy HH:mm"),
          t.transaction_id,
          t.customer?.full_name || "-",
          t.customer?.phone || "-",
          t.meter?.meter_id || t.meter_id,
          t.meter?.meter_brand || "-",
          t.amount,
          t.meter?.meter_brand === "VICTRON" ? 0 : feeAmount,
          t.meter?.meter_brand === "VICTRON" ? t.amount : electricityAmount,
          t.kwh_amount ? parseFloat(t.kwh_amount).toFixed(2) : "0",
          unitOf(t.meter),
          t.payment_method,
          t.token || "-",
          t.status === "completed" ? "Complétée" : t.status === "pending" ? "En attente" : t.status === "failed" ? "Échouée" : t.status,
          t.meter?.status === "suspended" ? "Bloqué" : t.meter?.status === "active" ? "Actif" : "-"
        ];
      });
      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const bom = "\uFEFF";
      const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
      const fileName = `transactions_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      saveAs(blob, fileName);
      toast.dismiss('csv-export');
      toast.success(`✅ CSV téléchargé : ${fileName}`);
    } catch (e) {
      console.error(e);
      toast.dismiss('csv-export');
      toast.error("❌ Erreur export CSV");
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!transactions || transactions.length === 0) {
        toast.error("Aucune transaction à exporter");
        return;
      }
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.padding = "30px";
      container.style.backgroundColor = "#ffffff";
      container.style.width = "1600px";
      const totalFees = transactions.reduce((sum, t) => sum + (t.meter?.meter_brand === "VICTRON" ? 0 : computeFeeBreakdown(t).feeAmount), 0);
      const totalElectricity = transactions.reduce((sum, t) => sum + (t.meter?.meter_brand === "VICTRON" ? parseFloat(t.amount) : computeFeeBreakdown(t).electricityAmount), 0);
      container.innerHTML = `
        <div style="font-family: Arial, Helvetica, sans-serif;">
          <div style="text-align:center;margin-bottom:25px;border-bottom:3px solid #2563eb;padding-bottom:15px;">
            <h1 style="margin:0;font-size:28px;color:#1e293b;font-weight:700;">MAD POWER</h1>
            <p style="margin:6px 0;font-size:14px;color:#64748b;">Rapport des Transactions</p>
            <p style="font-size:12px;color:#94a3b8;">${format(new Date(), "dd/MM/yyyy HH:mm")}</p>
          </div>
          <div style="display:flex;gap:15px;margin-bottom:20px;">
            <div style="flex:1;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:15px;text-align:center;">
              <p style="margin:0;font-size:11px;color:#64748b;">Total Encaissé</p>
              <p style="margin:4px 0 0 0;font-size:20px;font-weight:700;color:#1e40af;">
                ${new Intl.NumberFormat("fr-FR").format(transactions.reduce((s, t) => s + parseFloat(t.amount), 0))} FCFA
              </p>
            </div>
            <div style="flex:1;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:15px;text-align:center;">
              <p style="margin:0;font-size:11px;color:#64748b;">Total Redevances</p>
              <p style="margin:4px 0 0 0;font-size:20px;font-weight:700;color:#dc2626;">
                ${new Intl.NumberFormat("fr-FR").format(totalFees)} FCFA
              </p>
            </div>
            <div style="flex:1;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:15px;text-align:center;">
              <p style="margin:0;font-size:11px;color:#64748b;">Total Courant / Abonnements Vendus</p>
              <p style="margin:4px 0 0 0;font-size:20px;font-weight:700;color:#16a34a;">
                ${new Intl.NumberFormat("fr-FR").format(totalElectricity)} FCFA
              </p>
            </div>
            <div style="flex:1;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:15px;text-align:center;">
              <p style="margin:0;font-size:11px;color:#64748b;">Total kWh</p>
              <p style="margin:4px 0 0 0;font-size:20px;font-weight:700;color:#059669;">
                ${transactions.reduce((s, t) => s + (parseFloat(t.kwh_amount) || 0), 0).toFixed(2)} kWh
              </p>
            </div>
          </div>
          <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;font-size:10px;">
            <thead>
              <tr style="background:#4472C4;color:white;">
                <th style="padding:9px 8px;text-align:left;">Date</th>
                <th style="padding:9px 8px;text-align:left;">Transaction</th>
                <th style="padding:9px 8px;text-align:left;">Client</th>
                <th style="padding:9px 8px;text-align:left;">Compteur</th>
                <th style="padding:9px 8px;text-align:right;">Total (FCFA)</th>
                <th style="padding:9px 8px;text-align:right;background:#b91c1c;">Redevance (FCFA)</th>
                <th style="padding:9px 8px;text-align:right;background:#166534;">Courant (FCFA)</th>
                <th style="padding:9px 8px;text-align:right;">kWh</th>
                <th style="padding:9px 8px;text-align:left;">Méthode</th>
                <th style="padding:9px 8px;text-align:center;">Statut</th>
              </tr>
            </thead>
            <tbody>
              ${transactions.map((t, idx) => {
                const isVictron = t.meter?.meter_brand === "VICTRON";
                const { feeAmount, electricityAmount } = isVictron
                  ? { feeAmount: 0, electricityAmount: parseFloat(t.amount) }
                  : computeFeeBreakdown(t);
                return `
                  <tr style="background:${idx % 2 ? "#f8fafc" : "#ffffff"};">
                    <td style="padding:8px;white-space:nowrap;">${format(new Date(t.created_at), "dd/MM/yyyy HH:mm")}</td>
                    <td style="padding:8px;font-family:monospace;font-size:9px;">${t.transaction_id.slice(0, 14)}…</td>
                    <td style="padding:8px;">
                      <strong>${t.customer?.full_name || "-"}</strong><br/>
                      <span style="font-size:8px;color:#64748b;">${t.customer?.phone || ""}</span>
                    </td>
                    <td style="padding:8px;font-family:monospace;">${t.meter?.meter_id || t.meter_id}</td>
                    <td style="padding:8px;text-align:right;font-weight:700;color:#1e293b;">
                      ${new Intl.NumberFormat("fr-FR").format(t.amount)}
                    </td>
                    <td style="padding:8px;text-align:right;font-weight:700;color:${feeAmount > 0 ? '#dc2626' : '#94a3b8'};">
                      ${feeAmount > 0 ? new Intl.NumberFormat("fr-FR").format(feeAmount) : "0"}
                    </td>
                    <td style="padding:8px;text-align:right;font-weight:700;color:#16a34a;">
                      ${new Intl.NumberFormat("fr-FR").format(electricityAmount)}
                    </td>
                    <td style="padding:8px;text-align:right;color:#059669;">
                      ${t.kwh_amount ? parseFloat(t.kwh_amount).toFixed(2) : "-"}
                    </td>
                    <td style="padding:8px;text-transform:capitalize;">${t.payment_method}</td>
                    <td style="padding:8px;text-align:center;">
                      <span style="
                        padding:3px 8px;border-radius:10px;font-size:9px;font-weight:600;
                        ${t.status === "completed" ? "background:#dcfce7;color:#166534" :
                          t.status === "pending" ? "background:#fef3c7;color:#92400e" :
                          "background:#fee2e2;color:#991b1b"}
                      ">
                        ${t.status === "completed" ? "VALIDÉE" : t.status === "pending" ? "EN ATTENTE" : "ÉCHEC"}
                      </span>
                    </td>
                  </tr>
                `;
              }).join("")}
            </tbody>
            <tfoot>
              <tr style="background:#f1f5f9;border-top:2px solid #4472C4;font-weight:700;">
                <td colspan="4" style="padding:10px 8px;">TOTAUX (${transactions.length} transaction(s))</td>
                <td style="padding:10px 8px;text-align:right;color:#1e40af;">
                  ${new Intl.NumberFormat("fr-FR").format(transactions.reduce((s, t) => s + parseFloat(t.amount), 0))} FCFA
                </td>
                <td style="padding:10px 8px;text-align:right;color:#dc2626;">
                  ${new Intl.NumberFormat("fr-FR").format(totalFees)} FCFA
                </td>
                <td style="padding:10px 8px;text-align:right;color:#16a34a;">
                  ${new Intl.NumberFormat("fr-FR").format(totalElectricity)} FCFA
                </td>
                <td style="padding:10px 8px;text-align:right;color:#059669;">
                  ${transactions.reduce((s, t) => s + (parseFloat(t.kwh_amount) || 0), 0).toFixed(2)} kWh
                </td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      `;
      document.body.appendChild(container);
      const canvas = await html2canvas(container, {
        scale: 2.5,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        windowWidth: 1600
      });
      document.body.removeChild(container);
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 10;
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 20;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - 20;
      }
      pdf.save(`transactions_${Date.now()}.pdf`);
      toast.success("PDF exporté avec succès");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const handlePrintReceipt = async (txn) => {
    try {
      const isVictron = txn.meter?.meter_brand === "VICTRON";
      const { feeAmount, electricityAmount } = isVictron
        ? { feeAmount: 0, electricityAmount: parseFloat(txn.amount) }
        : computeFeeBreakdown(txn);
      const container = document.createElement("div");
      container.style.padding = "40px";
      container.style.width = "800px";
      container.style.fontFamily = "Arial, Helvetica, sans-serif";
      container.style.background = "#fff";
      container.innerHTML = `
        <div style="text-align:center; margin-bottom:30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="margin:0; font-size:32px; color:#1e293b; font-weight:700; letter-spacing: 1px;">MAD POWER</h1>
          <p style="margin:5px 0 0 0; font-size:16px; color:#64748b;">Reçu de Transaction</p>
        </div>
        <div style="background:#f8fafc; padding:25px; border-radius:8px; margin-bottom:25px;">
          <table style="width:100%; font-size:14px; line-height:2.2; border-collapse: collapse;">
            <tr>
              <td style="color:#64748b; padding:10px 15px; border-bottom: 1px solid #e2e8f0;"><strong>ID Transaction</strong></td>
              <td style="text-align:right; font-family:monospace; padding:10px 15px; border-bottom: 1px solid #e2e8f0;">${txn.transaction_id}</td>
            </tr>
            <tr>
              <td style="color:#64748b; padding:10px 15px; border-bottom: 1px solid #e2e8f0;"><strong>Compteur</strong></td>
              <td style="text-align:right; padding:10px 15px; border-bottom: 1px solid #e2e8f0; font-weight: 600;">${txn.meter?.meter_id || txn.meter_id}</td>
            </tr>
            <tr>
              <td style="color:#64748b; padding:10px 15px; border-bottom: 1px solid #e2e8f0;"><strong>Client</strong></td>
              <td style="text-align:right; padding:10px 15px; border-bottom: 1px solid #e2e8f0;">${txn.customer?.full_name || "-"}</td>
            </tr>
            <tr>
              <td style="color:#64748b; padding:10px 15px;"><strong>Méthode de Paiement</strong></td>
              <td style="text-align:right; padding:10px 15px; text-transform:capitalize;">${txn.payment_method}</td>
            </tr>
          </table>
        </div>
        <div style="background:#f1f5f9; padding:20px; border-radius:8px; margin-bottom:20px; border-left:4px solid #94a3b8;">
          <p style="margin:0 0 12px 0; font-weight:700; color:#475569; font-size:13px;">💰 DÉTAIL DU PAIEMENT</p>
          <table style="width:100%; border-collapse:collapse; font-size:14px;">
            <tr style="border-bottom:1px solid #e2e8f0;">
              <td style="padding:8px 0; color:#64748b;">Montant total payé</td>
              <td style="text-align:right; font-weight:700; padding:8px 0;">${new Intl.NumberFormat('fr-FR').format(txn.amount)} FCFA</td>
            </tr>
            ${!isVictron ? `
            <tr style="border-bottom:1px solid #e2e8f0;">
              <td style="padding:8px 0; color:#dc2626;">
                ${feeAmount > 0 ? '🔴 Redevance déduite' : '✅ Redevance'}
              </td>
              <td style="text-align:right; font-weight:700; color:${feeAmount > 0 ? '#dc2626' : '#94a3b8'}; padding:8px 0;">
                ${feeAmount > 0 ? new Intl.NumberFormat('fr-FR').format(feeAmount) + ' FCFA' : '0 FCFA (à jour)'}
              </td>
            </tr>
            <tr>
              <td style="padding:8px 0; color:#16a34a;">${isWaterMeter(txn.meter) ? "💧 Crédit eau" : "⚡ Crédit électricité"}</td>
              <td style="text-align:right; font-weight:700; color:#16a34a; padding:8px 0;">${new Intl.NumberFormat('fr-FR').format(electricityAmount)} FCFA</td>
            </tr>
            ` : `
            <tr>
              <td style="padding:8px 0; color:#16a34a;">⚡ Abonnement Victron</td>
              <td style="text-align:right; font-weight:700; color:#16a34a; padding:8px 0;">${new Intl.NumberFormat('fr-FR').format(electricityAmount)} FCFA</td>
            </tr>
            `}
          </table>
        </div>
        ${!isVictron ? `
        <div style="background:#dbeafe; padding:25px; border-radius:8px; margin-bottom:25px; border-left:4px solid #2563eb;">
          <table style="width:100%; font-size:16px; border-collapse: collapse;">
            <tr>
              <td style="padding:12px 15px; border-bottom: 1px solid #93c5fd;"><strong>${isWaterMeter(txn.meter) ? "Volume d'eau acheté (m³)" : "Énergie achetée (kWh)"}</strong></td>
              <td style="text-align:right; font-size:26px; font-weight:700; color:#059669; padding:12px 15px; border-bottom: 1px solid #93c5fd;">${txn.kwh_amount ? parseFloat(txn.kwh_amount).toFixed(isWaterMeter(txn.meter) ? 3 : 2) : "-"} ${unitOf(txn.meter)}</td>
            </tr>
          </table>
        </div>
        ` : ''}
        ${txn.token ? `
        <div style="background:#dcfce7; padding:25px; border-radius:8px; margin-bottom:25px; border:2px solid #10b981;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
            <p style="margin:0; color:#065f46; font-weight:700; font-size:15px;">🔑 TOKEN ${isVictron ? 'VICTRON PAYGO' : isWaterMeter(txn.meter) ? 'EAU' : 'STS'}</p>
            <p style="margin:0; color:#047857; font-size:11px;">À saisir dans le compteur</p>
          </div>
          <div style="background: white; padding: 15px; border-radius: 6px; border: 1px solid #86efac;">
            <p style="margin:0; font-family:monospace; font-size:20px; color:#047857; font-weight:700; word-break:break-all; letter-spacing: 2px; text-align: center;">${txn.token}</p>
          </div>
        </div>
        ` : ''}
        <div style="margin-top:30px; padding-top:25px; border-top:2px solid #e2e8f0;">
          <table style="width:100%; font-size:13px; color:#64748b; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px 0; border-bottom: 1px solid #f1f5f9;"><strong>Statut</strong></td>
              <td style="text-align:right; padding: 8px 0; border-bottom: 1px solid #f1f5f9;">
                ${txn.status === 'completed' ? '✓ Complétée' : txn.status === 'pending' ? '⏳ En attente' : '✗ Échouée'}
              </td>
            </tr>
            <tr>
              <td style="padding: 8px 0;"><strong>Date et Heure</strong></td>
              <td style="text-align:right; padding: 8px 0;">${new Date(txn.created_at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}</td>
            </tr>
          </table>
        </div>
        <div style="text-align:center; margin-top:40px; padding-top:25px; border-top:1px solid #e2e8f0;">
          <p style="margin:0; font-size:11px; color:#94a3b8;">Document généré le ${new Date().toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}</p>
          <p style="margin:10px 0 0 0; font-size:11px; color:#94a3b8; font-weight: 500;">MAD POWER - Système de Gestion d'Énergie Prépayée</p>
          <p style="margin:5px 0 0 0; font-size:10px; color:#cbd5e1;">Conservez ce reçu pour toute réclamation</p>
        </div>
      `;
      document.body.appendChild(container);
      const canvas = await html2canvas(container, { scale: 2.5, backgroundColor: '#ffffff', logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, Math.min(imgHeight, pdfHeight - 20));
      pdf.save(`recu_${txn.transaction_id}.pdf`);
      container.remove();
      toast.success("Reçu généré avec succès");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la génération du reçu");
    }
  };

  const openDetails = (txn) => { setSelectedTxn(txn); setDetailsOpen(true); };
  const copyToken = async (token) => {
    if (!token) { toast.warn("Aucun token à copier"); return; }
    try {
      await navigator.clipboard.writeText(token);
      toast.success("Token copié");
    } catch (e) {
      toast.error("Impossible de copier");
    }
  };

  const MeterBlockedAlert = ({ reason }) => (
    <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded-lg">
      <p className="font-semibold">🚫 Compteur bloqué</p>
      <p className="text-sm mt-1">{reason || "Veuillez régulariser vos redevances avant tout achat."}</p>
    </div>
  );

  // ✅ NOUVEAU : souscription du compteur Victron sélectionné (ou null si non configuré)
  const selectedVictronSub = isVictronMeter(selectedMeter)
    ? VICTRON_SUBSCRIPTIONS[selectedMeter.victron_subscription]
    : null;

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-slate-50 to-blue-50">
      <ToastContainer position="top-right" />
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Gestion des Transactions</h1>
            <p className="text-sm text-slate-600 mt-1">✅ Achat possible même si compteur bloqué (si montant ≥ dette)</p>
            <p className="text-sm text-slate-600 mt-1">Recherche, filtres, génération de token et export</p>
          </div>
          <div className="flex gap-2">
            <PermissionGuard permission={PERMISSIONS.EXPORT_REPORTS}>
              <button onClick={handleExportCSV} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2">
                <Download size={16} /> Export CSV
              </button>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.EXPORT_REPORTS}>
              <button onClick={handleExportPDF} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                <Download size={16} /> Export PDF
              </button>
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.CREATE_SALE}>
              <button onClick={openCreateModal} className="px-4 py-3 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 flex items-center gap-2">
                <Plus size={18} /> Nouvelle Vente
              </button>
            </PermissionGuard>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-600 mb-1">Recherche</label>
              <div className="relative">
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Client, compteur, token..." className="w-full px-4 py-2 border rounded-lg" />
                <IconSearch className="absolute right-3 top-3 text-slate-400" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Statut</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Tous</option>
                <option value="completed">Complétée</option>
                <option value="pending">En attente</option>
                <option value="failed">Échouée</option>
                <option value="cancelled">Annulée</option>  
                <option value="refunded">Remboursée</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Méthode de paiement</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="all">Toutes</option>
                <option value="cash">Cash</option>
                <option value="wave">Wave</option>
                <option value="orange_money">Orange Money</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Date début</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">Date fin</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden" ref={tableRef}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">ID</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Compteur</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Client</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Montant Total (FCFA)</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Montant Redevance (FCFA)</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-700">Montant du Courant (FCFA)</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">kWh / m³</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Token</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Méthode</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Statut</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">État Compteur</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Date</th>
                  <th className="px-4 py-3 text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={13} className="py-12 text-center">
                      <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="py-8 text-center text-slate-500">Aucune transaction</td>
                  </tr>
                ) : (
                  // transactions.map((t) => {
                  //   const isVictron = t.meter?.meter_brand === "VICTRON";
                  //   const { feeAmount, electricityAmount } = isVictron
                  //     ? { feeAmount: 0, electricityAmount: parseFloat(t.amount) }
                  //     : computeFeeBreakdown(t);
                    transactions.map((t) => {
                      const isVictron = t.meter?.meter_brand === "VICTRON";
                      const isCompleted = t.status === "completed";
                      const { feeAmount, electricityAmount } = isVictron
                        ? { feeAmount: 0, electricityAmount: parseFloat(t.amount) }
                        : computeFeeBreakdown(t);
                    return (
                      <tr key={t.id} className="hover:bg-slate-50 transition">
                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{t.transaction_id.slice(0, 14)}…</td>
                        <td className="px-4 py-3">
                          <span className="font-medium">{t.meter?.meter_id || t.meter_id}</span>
                          <p>
                            <span className="ml-1 text-xs text-slate-400">{t.meter?.meter_brand}</span>
                            {isVictron && <Zap size={12} className="inline ml-1 text-amber-500" />}
                            {isWaterMeter(t.meter) && <span className="ml-1 text-xs text-cyan-600">💧 Eau</span>}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-sm">{t.customer?.full_name || "-"}</div>
                          <div className="text-xs text-slate-400">{t.customer?.phone || ""}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">
                          {new Intl.NumberFormat('fr-FR').format(t.amount)}
                        </td>
                        <td className="px-4 py-3 text-right bg-red-50">
                          {!isVictron && !isCompleted ? (
                            <span className="text-slate-400">-</span>
                          ) : feeAmount > 0 ? (
                            <span className="font-bold text-red-600">
                              {new Intl.NumberFormat('fr-FR').format(feeAmount)}
                            </span>
                          ) : (
                            <span className="text-slate-400 font-medium">0</span>
                          )}
                        </td>
                        {/* <td className="px-4 py-3 text-right bg-red-50">
                          {feeAmount > 0 ? (
                            <span className="font-bold text-red-600">
                              {new Intl.NumberFormat('fr-FR').format(feeAmount)}
                            </span>
                          ) : (
                            <span className="text-slate-400 font-medium">0</span>
                          )}
                        </td> */}
                        <td className="px-4 py-3 text-right bg-green-50">
                          {!isVictron && !isCompleted ? (
                            <span className="text-slate-400">-</span>
                          ) : (
                            <span className="font-bold text-green-700">
                              {new Intl.NumberFormat('fr-FR').format(electricityAmount)}
                            </span>
                          )}
                        </td>
                        {/* <td className="px-4 py-3 text-right bg-green-50">
                          <span className="font-bold text-green-700">
                            {new Intl.NumberFormat('fr-FR').format(electricityAmount)}
                          </span>
                        </td> */}
                        <td className="px-4 py-3 text-emerald-600 font-medium">
                          {isVictron ? (
                            <span className="text-xs text-slate-400">Abonnement</span>
                          ) : (
                            <>{t.kwh_amount ? parseFloat(t.kwh_amount).toFixed(2) : "-"} {unitOf(t.meter)}</>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-slate-600">
                          {t.token ? t.token.substring(0, 12) + "..." : "-"}
                        </td>
                        <td className="px-4 py-3 capitalize text-sm">{t.payment_method}</td>
                        <td className="px-4 py-3">
                          {/* {t.status === "completed" ? <Badge text="Complétée" tone="green" /> :
                            t.status === "pending" ? <Badge text="En attente" tone="yellow" /> :
                              t.status === "failed" ? <Badge text="Échouée" tone="red" /> :
                                <Badge text={t.status} />} */}
                            {t.status === "completed" ? <Badge text="Complétée" tone="green" /> :
                              t.status === "pending" ? <Badge text="En attente" tone="yellow" /> :
                                t.status === "failed" ? <Badge text="Échouée" tone="red" /> :
                                  t.status === "cancelled" ? <Badge text="Annulée" tone="slate" /> :
                                    <Badge text={t.status} />}
                        </td>
                        <td className="px-4 py-2">
                          <MeterStatusBadge status={t.meter?.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                          {format(new Date(t.created_at), "dd/MM/yyyy HH:mm")}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={() => handlePrintReceipt(t)} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" title="Imprimer reçu">
                              <Printer size={14} />
                            </button>
                            <button onClick={() => openDetails(t)} className="px-2 py-1 rounded bg-blue-50 hover:bg-blue-100" title="Voir détails">
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            itemsPerPage={PAGE_SIZE}
          />
        </div>
        {/* MODAL CREATE */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-6 overflow-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Nouvelle transaction</h3>
                <button onClick={() => {
                  setModalOpen(false);
                  setDebtInfo(null);
                  setMeterBlocked(false);
                  setBlockReason("");
                  setLoadingDebt(false);
                  setAmountError("");
                  setVictronMonths(1);
                }} className="p-2 rounded hover:bg-slate-100"><X /></button>
              </div>
              <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Compteur</label>
                    <div className="relative mb-2">
                      <input
                        type="text"
                        placeholder="🔍 Tapez au moins 2 caractères pour rechercher..."
                        value={meterSearch}
                        onChange={(e) => setMeterSearch(e.target.value)}
                        className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {meterSearch && (
                        <button onClick={() => { setMeterSearch(""); setMeters([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                    {!meterSearch && !loadingMeters && (
                      <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        💡 Tapez le nom du client ou le numéro de compteur pour rechercher
                      </div>
                    )}
                    {loadingMeters && (
                      <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                        <Loader className="inline w-4 h-4 animate-spin mr-2" />Recherche en cours...
                      </div>
                    )}
                    {meterSearch.length > 0 && meterSearch.length < 2 && !loadingMeters && (
                      <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                        ⚠️ Tapez au moins 2 caractères pour lancer la recherche
                      </div>
                    )}
                    <select
                      value={form.meter_id}
                      onChange={(e) => onChangeMeter(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      disabled={loadingMeters || meterSearch.length < 2}
                    >
                      <option value="">{meterSearch.length < 2 ? "-- Recherchez d'abord un compteur --" : "-- Choisir compteur --"}</option>
                      {meterSearch.length >= 2 && meters.length === 0 && !loadingMeters && (
                        <option disabled>Aucun compteur trouvé pour "{meterSearch}"</option>
                      )}
                      {meters.map((m) => (
                        <option key={m.meter_id} value={m.meter_id}>
                          {m.meter_id} — {m.customer?.full_name || "Sans client"}
                          {m.meter_brand === "VICTRON" && " ⚡ VICTRON"}
                          {isWaterMeter(m) && " 💧 EAU"}
                          {m.status === "suspended" && " 🔒 BLOQUÉ"}
                        </option>
                      ))}
                    </select>
                    {meterSearch.length >= 2 && meters.length > 0 && !loadingMeters && (
                      <p className="mt-1 text-xs text-green-600">✓ {meters.length} compteur(s) trouvé(s)</p>
                    )}
                    {meters.length === 50 && !loadingMeters && (
                      <p className="mt-1 text-xs text-orange-600">⚠️ Plus de 50 résultats. Affinez votre recherche.</p>
                    )}
                  </div>
                  {loadingDebt && (
                    <div className="flex items-center justify-center p-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Vérification des redevances...</span>
                    </div>
                  )}
                  {debtInfo && <DebtAlert debtInfo={debtInfo} />}
                  {selectedMeter && (
                    <div className="bg-blue-50 p-3 rounded-lg border">
                      <p className="font-semibold">{selectedMeter.customer?.full_name}</p>
                      <p className="text-sm text-slate-600">{selectedMeter.customer?.phone || "-"}</p>
                      {/* ✅ CORRIGÉ : "Zone" n'existe plus dans le modèle (renommé en Département/Commune) */}
                      <p className="text-sm">
                        Localisation: {selectedMeter.departement || "-"}
                        {selectedMeter.commune ? ` · ${selectedMeter.commune}` : ""}
                      </p>
                      {!isVictronMeter(selectedMeter) && (
                        <p className="text-sm">
                          Tarif: {selectedMeter.tariff || SENEGAL_TARIF_KWH} FCFA/{unitOf(selectedMeter)}
                          {isWaterMeter(selectedMeter) && <span className="ml-2 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-semibold">💧 Compteur à eau</span>}
                        </p>
                      )}
                      <p className="text-sm">Statut: {selectedMeter.status === "suspended" ? "🔒 BLOQUÉ" : "✅ Actif"}</p>
                    </div>
                  )}
                  {meterBlocked && <MeterBlockedAlert reason={blockReason} />}

                  <div>
                    {isVictronMeter(selectedMeter) ? (
                      // ✅ CORRIGÉ : achat Victron par abonnement (S1/S2/S3 x nombre de mois)
                      // au lieu des anciens forfaits libres qui ne correspondaient pas au backend.
                      <div>
                        <label className="block text-sm text-slate-600 mb-2 flex items-center gap-1">
                          <Zap size={16} className="text-amber-500" /> Abonnement Victron PAYGO
                        </label>
                        {!selectedVictronSub ? (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-red-700 font-semibold">
                              ⚠️ Ce compteur Victron n'a pas de souscription configurée.
                            </p>
                            <p className="text-xs text-red-600 mt-1">
                              Allez dans <strong>Compteurs → Modifier ce compteur</strong> pour définir
                              son numéro de série, sa clé privée et sa souscription (S1/S2/S3).
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                              <p className="text-sm font-semibold text-amber-800">
                                {selectedVictronSub.label} — {selectedVictronSub.price.toLocaleString()} FCFA / mois
                              </p>
                            </div>
                            <label className="block text-xs text-slate-600 mb-1">Nombre de mois</label>
                            <div className="grid grid-cols-4 gap-2">
                              {[1, 2, 3, 6].map((m) => (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => setVictronMonths(m)}
                                  className={`p-3 rounded-lg border-2 text-center transition-colors ${
                                    victronMonths === m
                                      ? "border-blue-600 bg-blue-50 font-bold text-blue-700"
                                      : "border-gray-200 hover:border-blue-300"
                                  }`}
                                >
                                  {m} mois
                                </button>
                              ))}
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                              <label className="text-xs text-slate-600">Ou saisir manuellement :</label>
                              <input
                                type="number"
                                min={1}
                                value={victronMonths}
                                onChange={(e) => setVictronMonths(Math.max(1, parseInt(e.target.value) || 1))}
                                className="w-20 px-2 py-1 border rounded-lg text-center"
                              />
                            </div>
                            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex justify-between items-center">
                              <span className="text-sm text-emerald-800">
                                {victronMonths} mois × {selectedVictronSub.price.toLocaleString()} FCFA
                              </span>
                              <span className="font-bold text-emerald-800">
                                {new Intl.NumberFormat('fr-FR').format(form.amount || 0)} FCFA
                              </span>
                            </div>
                          </>
                        )}
                        {amountError && (
                          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm font-semibold text-red-700">{amountError}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm text-slate-600 mb-1">
                          Montant (FCFA) - <span className="text-red-600">Minimum: {MINIMUM_PURCHASE_AMOUNT} FCFA</span>
                        </label>
                        <input
                          type="number"
                          value={form.amount}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm({ ...form, amount: value });
                            validateAmount(value);
                          }}
                          min={MINIMUM_PURCHASE_AMOUNT}
                          step="100"
                          className={`w-full px-3 py-2 border rounded-lg ${amountError ? 'border-red-500' : 'focus:ring-blue-500'}`}
                          placeholder={`Minimum: ${MINIMUM_PURCHASE_AMOUNT} FCFA`}
                        />
                        {amountError && (
                          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm font-semibold text-red-700">{amountError}</p>
                          </div>
                        )}
                        {!amountError && form.amount && parseFloat(form.amount) >= MINIMUM_PURCHASE_AMOUNT && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-xs text-green-700">✓ Montant valide</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {!isVictronMeter(selectedMeter) && debtInfo && debtInfo.total_debt > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Montant total:</span>
                        <span className="font-bold">{form.amount} FCFA</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-red-700">- Redevances:</span>
                        <span className="font-bold text-red-700">{Math.min(parseFloat(form.amount) || 0, debtInfo.total_debt).toFixed(0)} FCFA</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-yellow-300 pt-2">
                        <span className="text-green-700">= Pour électricité:</span>
                        <span className="font-bold text-green-700">{Math.max(0, (parseFloat(form.amount) || 0) - debtInfo.total_debt).toFixed(0)} FCFA</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">≈ kWh estimé:</span>
                        <span className="font-bold text-blue-700">
                          {((Math.max(0, (parseFloat(form.amount) || 0) - debtInfo.total_debt)) / (selectedMeter?.tariff || SENEGAL_TARIF_KWH)).toFixed(2)} kWh
                        </span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Méthode de paiement</label>
                    <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
                      <option value="cash">Cash</option>
                      <option value="orange_money">Orange Money</option>
                    </select>
                  </div>

                  {!isVictronMeter(selectedMeter) && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-sm text-slate-600">{isWaterMeter(selectedMeter) ? "Volume estimé" : "kWh estimé"}</p>
                      <p className="font-semibold text-blue-700">{estimatedKwh ? estimatedKwh.toFixed(isWaterMeter(selectedMeter) ? 3 : 2) : "-"} {unitOf(selectedMeter)}</p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={onConfirm}
                      disabled={
                        !form.meter_id || !form.amount || isSubmitting || !!amountError ||
                        (isVictronMeter(selectedMeter) ? !selectedVictronSub : parseFloat(form.amount) < MINIMUM_PURCHASE_AMOUNT)
                      }
                      className={`px-4 py-2 rounded-lg text-white ${
                        !form.meter_id || !form.amount || isSubmitting || !!amountError ||
                        (isVictronMeter(selectedMeter) ? !selectedVictronSub : parseFloat(form.amount) < MINIMUM_PURCHASE_AMOUNT)
                          ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'}`}
                    >
                      {isSubmitting ? "Traitement..." : "Valider"}
                    </button>
                    <button onClick={() => { setForm({ meter_id: "", amount: "", payment_method: "cash" }); setSelectedMeter(null); setAmountError(""); setVictronMonths(1); }} className="px-4 py-2 border rounded-lg">
                      Réinitialiser
                    </button>
                  </div>
                </div>
                <aside className="space-y-4">
                  <div className="bg-white border rounded-lg p-3 shadow-sm">
                    <p className="text-sm text-slate-500">Historique rapide</p>
                    <div className="mt-3 space-y-2 max-h-48 overflow-auto">
                      {historyForMeter.length === 0 ? <p className="text-xs text-slate-400">Aucune donnée</p> :
                        historyForMeter.map(h => (
                          <div key={h.id} className="flex justify-between items-center">
                            <div>
                              <p className="text-sm font-medium">{h.transaction_id}</p>
                              <p className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{new Intl.NumberFormat('fr-FR').format(h.amount)} FCFA</p>
                              <p className="text-xs text-emerald-600">{parseFloat(h.kwh_amount || 0).toFixed(2)} {unitOf(selectedMeter)}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                  {!isVictronMeter(selectedMeter) && debtInfo && debtInfo.total_debt > 0 && parseFloat(form.amount) < debtInfo.total_debt && (
                    <div className="bg-red-100 border-2 border-red-400 rounded-lg p-3 animate-pulse">
                      <p className="text-sm font-bold text-red-900 text-center">⚠️ MONTANT INSUFFISANT</p>
                      <p className="text-xs text-red-800 text-center mt-1">Minimum requis: {new Intl.NumberFormat('fr-FR').format(debtInfo.total_debt)} FCFA</p>
                    </div>
                  )}
                  <div className="bg-white border rounded-lg p-3 shadow-sm">
                    <p className="text-sm text-slate-500">Actions</p>
                    <div className="mt-3 flex flex-col gap-2">
                      <button onClick={() => { if (form.meter_id) fetchMeterDetail(form.meter_id); }} className="px-3 py-2 bg-slate-100 rounded-lg">Charger détails compteur</button>
                      {!isVictronMeter(selectedMeter) && (
                        <button onClick={() => { if (selectedMeter) setForm(f => ({ ...f, amount: ((selectedMeter.tariff || SENEGAL_TARIF_KWH) * 5).toFixed(0) })); }} className="px-3 py-2 border rounded-lg">Ex: crédit 5 kWh</button>
                      )}
                      <button onClick={() => { setModalOpen(false); setDebtInfo(null); setMeterBlocked(false); setBlockReason(""); setLoadingDebt(false); }} className="px-3 py-2 border rounded-lg">Fermer</button>
                    </div>
                  </div>
                  {!isVictronMeter(selectedMeter) && (
                    <div className="bg-white border rounded-lg p-3 shadow-sm">
                      <p className="text-sm text-slate-500 mb-3">Montants rapides</p>
                      <div className="flex flex-col gap-2">
                        {["1000", "2000", "5000"].map(amt => (
                          <button key={amt} onClick={() => setForm(f => ({ ...f, amount: amt }))} className="px-3 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm">
                            {new Intl.NumberFormat('fr-FR').format(parseInt(amt))} FCFA
                          </button>
                        ))}
                        {selectedMeter && (
                          <button onClick={() => {
                            const tarif = selectedMeter.tariff || SENEGAL_TARIF_KWH;
                            const electricityAmount = tarif * 5;
                            const totalWithDebt = debtInfo ? electricityAmount + debtInfo.total_debt : electricityAmount;
                            const rounded = Math.ceil(totalWithDebt / 100) * 100;
                            setForm(f => ({ ...f, amount: rounded.toString() }));
                          }} className="px-3 py-2 bg-green-50 hover:bg-green-100 rounded-lg text-sm">
                            ⚡ 5 kWh {debtInfo && debtInfo.total_debt > 0 && '+ dette'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {!isVictronMeter(selectedMeter) && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-yellow-800">💡 Montant minimum</p>
                      <p className="text-sm font-bold text-yellow-900 mt-1">{MINIMUM_PURCHASE_AMOUNT} FCFA</p>
                      <p className="text-xs text-yellow-700 mt-1">Tout achat doit être d'au moins {MINIMUM_PURCHASE_AMOUNT} FCFA</p>
                    </div>
                  )}
                </aside>
              </div>
            </div>
          </div>
        )}
        {/* CONFIRMATION */}
        {confirmOpen && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full">
              <h4 className="text-lg font-semibold">Résumé avant validation</h4>
              <div className="mt-3 space-y-2">
                <p><strong>Compteur:</strong> {form.meter_id}</p>
                <p><strong>Client:</strong> {selectedMeter?.customer?.full_name || "-"}</p>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="font-semibold text-blue-900 mb-2">💰 Détail du paiement</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total à payer:</span>
                      <span className="font-bold">{new Intl.NumberFormat("fr-FR").format(form.amount)} FCFA</span>
                    </div>
                    {/* ✅ CORRIGÉ : résumé basé sur la souscription, plus sur VICTRON_FORFAITS_UI */}
                    {isVictronMeter(selectedMeter) ? (
                      <div className="flex justify-between">
                        <span>Abonnement:</span>
                        <span className="font-bold">
                          {selectedVictronSub?.label} — {victronMonths} mois
                        </span>
                      </div>
                    ) : (
                      <>
                        {debtInfo && debtInfo.total_debt > 0 && (
                          <>
                            <div className="flex justify-between text-red-600">
                              <span>🔴 Redevance:</span>
                              <span className="font-bold">{debtInfo.total_debt.toFixed(0)} FCFA</span>
                            </div>
                            <div className="flex justify-between text-green-600 pt-1 border-t">
                              <span>⚡ Électricité:</span>
                              <span className="font-bold">{Math.max(0, parseFloat(form.amount) - debtInfo.total_debt).toFixed(0)} FCFA</span>
                            </div>
                          </>
                        )}
                        {(!debtInfo || debtInfo.total_debt === 0) && (
                          <>
                            <div className="flex justify-between text-slate-400">
                              <span>🔴 Redevance:</span>
                              <span className="font-bold">0 FCFA (à jour)</span>
                            </div>
                            <div className="flex justify-between text-green-600 pt-1 border-t">
                              <span>{isWaterMeter(selectedMeter) ? "💧 Eau:" : "⚡ Électricité:"}</span>
                              <span className="font-bold">{form.amount} FCFA</span>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {!isVictronMeter(selectedMeter) && (
                  <p><strong>{isWaterMeter(selectedMeter) ? "Volume estimé" : "kWh estimé"}:</strong> {estimatedKwh ? estimatedKwh.toFixed(isWaterMeter(selectedMeter) ? 3 : 2) : "-"} {unitOf(selectedMeter)}</p>
                )}
                <p><strong>Méthode:</strong> {form.payment_method}</p>
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 mt-3">
                  <p className="text-sm font-semibold text-green-800">✅ Montant conforme</p>
                </div>
              </div>
              <div className="mt-4 flex gap-3 justify-end">
                <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 border rounded-lg">Annuler</button>
                <button onClick={submitTransaction} disabled={isSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  {isSubmitting ? "Traitement..." : "Confirmer & Générer"}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* DETAILS */}
        {detailsOpen && selectedTxn && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full">
              <div className="flex items-start justify-between">
                <h3 className="text-lg font-semibold">Détails de la transaction</h3>
                <button onClick={() => { setDetailsOpen(false); setSelectedTxn(null); }} className="p-2 rounded hover:bg-slate-100"><X /></button>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-slate-500">ID</p><p className="font-mono text-sm">{selectedTxn.transaction_id}</p></div>
                  <div><p className="text-xs text-slate-500">Statut</p><p>{selectedTxn.status}</p></div>
                  <div><p className="text-xs text-slate-500">Compteur</p><p className="font-semibold">{selectedTxn.meter?.meter_id || selectedTxn.meter_id}</p></div>
                  <div><p className="text-xs text-slate-500">Client</p><p>{selectedTxn.customer?.full_name || "-"}</p></div>
                  <div>
                    <p className="text-xs text-slate-500">Montant Total</p>
                    <p className="font-semibold">{new Intl.NumberFormat('fr-FR').format(selectedTxn.amount)} FCFA</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{isWaterMeter(selectedTxn.meter) ? "Volume" : "kWh"}</p>
                    <p className="font-semibold text-emerald-600">
                      {selectedTxn.meter?.meter_brand === "VICTRON"
                        ? "Abonnement"
                        : (selectedTxn.kwh_amount ? parseFloat(selectedTxn.kwh_amount).toFixed(2) : "-") + " " + unitOf(selectedTxn.meter)}
                    </p>
                  </div>
                </div>
                {(() => {
                  const isVictron = selectedTxn.meter?.meter_brand === "VICTRON";
                  const { feeAmount, electricityAmount } = isVictron
                    ? { feeAmount: 0, electricityAmount: parseFloat(selectedTxn.amount) }
                    : computeFeeBreakdown(selectedTxn);
                  return (
                    <div className="bg-slate-50 rounded-lg p-3 border">
                      <p className="text-xs font-semibold text-slate-600 mb-2">💰 Détail du paiement</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-red-50 rounded p-2 text-center">
                          <p className="text-xs text-slate-500">Redevance</p>
                          <p className={`font-bold ${feeAmount > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                            {feeAmount > 0 ? new Intl.NumberFormat('fr-FR').format(feeAmount) + ' FCFA' : '0 FCFA'}
                          </p>
                        </div>
                        <div className="bg-green-50 rounded p-2 text-center">
                          <p className="text-xs text-slate-500">{isVictron ? "Abonnement" : "Courant"}</p>
                          <p className="font-bold text-green-700">{new Intl.NumberFormat('fr-FR').format(electricityAmount)} FCFA</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {selectedTxn.token && (
                  <div className="bg-green-50 p-3 rounded border">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-green-800">Token {selectedTxn.meter?.meter_brand === "VICTRON" ? "Victron PAYGO" : isWaterMeter(selectedTxn.meter) ? "Eau" : "STS"}</p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => copyToken(selectedTxn.token)} className="px-2 py-1 rounded bg-green-100 hover:bg-green-200"><Copy size={14} /></button>
                        <button onClick={() => handlePrintReceipt(selectedTxn)} className="px-2 py-1 rounded bg-green-100 hover:bg-green-200"><Printer size={14} /></button>
                      </div>
                    </div>
                    <p className="font-mono mt-2 break-all text-green-700">{selectedTxn.token}</p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setDetailsOpen(false); setSelectedTxn(null); }} className="px-4 py-2 border rounded-lg">Fermer</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default Transactions;